import { AfterViewInit, Component, inject, Inject, ViewChild } from '@angular/core';
import { getIdToken } from '@angular/fire/auth';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { CustomerComponent } from '../customer/shared/customer.component';
import { Customer } from '../customer/shared/customer.model';
import { DealerRepComponent } from '../dealer-rep/shared/dealer-rep.component';
import { DealerRep } from '../dealer-rep/shared/dealer-rep.model';
import { DealerComponent } from '../dealer/shared/dealer.component';
import { Dealer } from '../dealer/shared/dealer.model';
import { EmployeeComponent } from '../employee/shared/employee.component';
import { Employee } from '../employee/shared/employee.model';
import { BaseComponent } from '../shared/base.component';
import { StateManager } from '../shared/state-manager.class';
import { KnownUser } from '../user/known-user.model';
import { UserAccountUtil } from './user-account-util';
import { UserAccountService } from './user-account.service';

export interface DialogData {
    name: string;
}

@Component({
    selector: 'user-account-popup',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        MatButtonModule,
        EmployeeComponent,
        DealerComponent,
        DealerRepComponent,
        CustomerComponent
    ],
    templateUrl: 'user-account-popup.component.html',
    styleUrl: 'user-account-popup.component.css'
})
export class UserAccountPopup extends BaseComponent implements AfterViewInit{

    @ViewChild(EmployeeComponent) employeeComponent!:EmployeeComponent;
    @ViewChild(DealerComponent) dealerComponent!:DealerComponent;
    @ViewChild(DealerRepComponent) dealerRepComponent!:DealerRepComponent;
    @ViewChild(CustomerComponent) customerComponent!:CustomerComponent;

    private userAccountService: UserAccountService = inject(UserAccountService);

    private static _retrievingUserAccount = "Retrieving User Account";
    private static _retrievingUserAccountError = `${UserAccountPopup._retrievingUserAccount} error:`;
    private static _updatingUserAccount = "Updating User Account";
    private static _updatingUserAccountError = `${UserAccountPopup._updatingUserAccount} error:`;

    public constructor(@Inject(MatDialogRef) public matDialogRef: MatDialogRef<UserAccountPopup>,
                       @Inject(MAT_DIALOG_DATA) public data: DialogData) {
        super();
    }

    public ngAfterViewInit() {
        const userType = this.userType;

        if (userType == "employee") {
            this.employeeComponent.init();
            this.employeeComponent.populateFormFromModel((<Employee>this.stateManager.getUserAccount()));
        } else if (userType == "dealer") {
            this.dealerComponent.init();
            this.dealerComponent.populateFormFromModel((<Dealer>this.stateManager.getUserAccount()));
        } else if (userType == "dealer-rep") {
            this.dealerRepComponent.populateFormFromModel((<DealerRep>this.stateManager.getUserAccount()));
        } else if (userType == "customer") {
            this.customerComponent.init();
            this.customerComponent.populateFormFromModel((<Customer>this.stateManager.getUserAccount()));
        } else {
            console.error(`Invalid User Type: ${userType}`);
        }
    }

    public async save() {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            let userAccount:KnownUser = <KnownUser>this.stateManager.getUserAccount();
            if (userAccount instanceof Employee) {
                userAccount = this.employeeComponent.createModelFromForm(userAccount);
            } else if (userAccount instanceof Dealer) {
                userAccount = this.dealerComponent.createModelFromForm(userAccount);
            } else if (userAccount instanceof DealerRep) {
                userAccount = this.dealerRepComponent.createModelFromForm(userAccount);
            } else if (userAccount instanceof Customer) {
                userAccount = this.customerComponent.createModelFromForm(userAccount);
            }

            const observable = this.userAccountService.updateUserAccount(await getIdToken(currentUser),
                                            this.userType, <KnownUser>this.stateManager.getUserAccount());
            super.message = "";
            super.showSnackBar(`${UserAccountPopup._updatingUserAccount}...`, -1);
                                
            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["update-user-account"] == "success") {
                        // Reload UserAccount to be sure the database
                        // truly reflects a successful update.
                        this.reloadUserAccount();
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${UserAccountPopup._updatingUserAccountError} ${(<Error>error).message}`;
                    this.message = message;
                    super.dismissSnackBar();
                }
            })
        } else {
            const message = `${UserAccountPopup._updatingUserAccountError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    public close(): void {
        this.matDialogRef.close();
    }

    public async reloadUserAccount() {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = this.userAccountService.getUserAccount(await getIdToken(currentUser));

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));

                    if (json["get-user-account"] == "success") {
                        const userAccount = <KnownUser>UserAccountUtil.createUserAcountInstance(json["user-type"], json["user-account"]);
                        this.stateManager.setUserAccount(userAccount);
                        this.showSnackBar("User Account updated.");
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error: any) {
                    const message = `${UserAccountPopup._retrievingUserAccountError} ${(<Error>error).message}`;
                    this.message = message;
                    super.dismissSnackBar();
               }
            })
        } else {
            const message = `${UserAccountPopup._retrievingUserAccountError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }
}
